import { type MouseEvent } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import { AddonComparisonCard } from '@/features/addonComparison/AddonComparisonCard'
import { type ProductOfferFragment, useOfferAddonSelectMutation } from '@/services/graphql/generated'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Card } from 'ui/src/components/Card/Card'
import { ResizablePanel } from 'ui/src/components/ResizeablePanel'
import { Select } from 'ui/src/components/Select/Select'
import { Text } from 'ui/src/components/Text/Text'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { SectionSubtitle, SectionTitle } from '../../SectionHeading'
import { useSelectedOffer } from '../../useSelectedOffer'
import { CardRadioGroup } from '../CardRadioGroup/CardRadioGroup'
import { description, textTransition, title } from '../CardRadioGroup/CardRadioGroup.css'
import { coverageSelectorItemBadge } from './TravelAddonSelector.css'

const baseTitle = 'travel-addon-base-title'
const baseContent = 'travel-addon-base-content'
const plusTitle = 'travel-addon-plus-title'
const plusPrice = 'travel-addon-plus-price'
const plusContent = 'travel-addon-plus-content'

type Params = {
  addons: Array<ProductOfferFragment['addons'][number]>
}

export function TravelAddonsSelector({ addons }: Params) {
  const { t } = useTranslation('purchase-form')
  const [selectedOffer] = useSelectedOffer()

  const [selectAddon] = useOfferAddonSelectMutation()

  const travelAddons = addons.filter((addon) => addon.variant.product === 'SE_TRAVEL_ADDON')
  const selectedAddon = travelAddons.find((addon) => addon.isSelected)

  if (travelAddons.length === 0 || !selectedOffer) return null

  const isAddonSelected = !!selectedAddon?.id

  const presentedAddon = selectedAddon ?? travelAddons[0]

  const presentedAddonPrice = {
    currencyCode: presentedAddon.grossCost.currencyCode,
    amount: presentedAddon.grossCost.amount,
  }

  const handleAddonSelection = (id: string) => {
    selectAddon({
      variables: {
        productOfferId: selectedOffer.id,
        addonIds: id === 'travel_base' ? [] : [id],
      },
      optimisticResponse: {
        productOfferAddonsSelect: {
          productOffers: [
            {
              ...selectedOffer,
              addons: selectedOffer.addons.map((addon) => ({
                ...addon,
                isSelected: addon.id === id,
              })),
            },
          ],
        },
      },
    })
  }

  const handleAddonRemoval = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    handleAddonSelection('travel_base')
  }

  const travelAddonOptions = travelAddons.map((addon) => ({
    id: addon.id,
    displayName: addon.coverageText,
    price: { currencyCode: addon.grossCost.currencyCode, amount: addon.grossCost.amount },
  }))

  return (
    <section className={yStack()}>
      <SectionTitle>{t('TRAVEL_ADDON_SECTION_TITLE')}</SectionTitle>
      <SectionSubtitle className={sprinkles({ marginBottom: 'lg' })}>
        {t('TRAVEL_ADDON_SECTION_SUBTITLE')}
      </SectionSubtitle>

      <CardRadioGroup.Root value={selectedAddon?.id || 'travel_base'} onValueChange={handleAddonSelection}>
        <CardRadioGroup.Item value="travel_base" aria-labelledby={baseTitle} aria-describedby={baseContent}>
          <Card.Aside floating>
            <CardRadioGroup.Badge isSelected={!isAddonSelected}>{t('TRAVEL_ADDON_BASE_BADGE')}</CardRadioGroup.Badge>
          </Card.Aside>
          <Card.Header>
            <Card.Heading>
              <Card.Title id={baseTitle} as="h2" className={title}>
                <Text as="span" size="xl">
                  {t('TRAVEL_ADDON_BASE_TITLE')}
                </Text>
              </Card.Title>
            </Card.Heading>
          </Card.Header>
          <Card.Content id={baseContent}>
            <Text className={description}>{t('TRAVEL_ADDON_BASE_DESCRIPTION')}</Text>
          </Card.Content>
        </CardRadioGroup.Item>

        <CardRadioGroup.Item
          key={presentedAddon.id}
          value={presentedAddon.id}
          aria-labelledby={plusTitle}
          aria-describedby={`${plusPrice} ${plusContent}`}
        >
          <Card.Aside floating>
            <CardRadioGroup.Badge isSelected={isAddonSelected}>
              <span id={plusPrice}>
                <AccessiblePrice price={presentedAddonPrice} prefix="+" />
              </span>
            </CardRadioGroup.Badge>
          </Card.Aside>

          <Card.Header>
            <Card.Heading>
              <Card.Title id={plusTitle} as="h2" className={title}>
                <Text as="span" size="xl">
                  {presentedAddon.variant.displayName}
                </Text>
              </Card.Title>
            </Card.Heading>
          </Card.Header>
          <ResizablePanel>
            <Card.Content id={plusContent} className={yStack({ gap: 'md' })}>
              <Text className={description}>{presentedAddon.description}</Text>

              {presentedAddon.usps.length > 0 && (
                <ul className={yStack({ gap: 'xs' })}>
                  {presentedAddon.usps.map((usp) => (
                    <li key={usp} className={xStack({ gap: 'md', alignItems: 'flex-start' })}>
                      <CheckIcon
                        size="1rem"
                        // margin top is applied to visually align the icon with the text
                        className={clsx(sprinkles({ marginTop: 'xxxs' }), description)}
                      />
                      <Text className={textTransition}>{usp}</Text>
                    </li>
                  ))}
                </ul>
              )}

              {isAddonSelected ? (
                <>
                  <Select.Root value={presentedAddon.id} onValueChange={handleAddonSelection}>
                    <Select.Trigger>
                      <Select.Label>{t('TRAVEL_ADDON_PLUS_COVERAGE_LABEL')}</Select.Label>
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content data-theme="dark">
                      <Select.Options>
                        {travelAddonOptions.map((addon) => (
                          <Select.Item key={addon.id} value={addon.id} asChild>
                            <div className={xStack({ justifyContent: 'space-between' })}>
                              <Select.ItemText>{addon.displayName}</Select.ItemText>
                              <Badge variant="grey1" className={coverageSelectorItemBadge}>
                                <AccessiblePrice price={addon.price} prefix="+" />
                              </Badge>
                            </div>
                          </Select.Item>
                        ))}
                      </Select.Options>
                    </Select.Content>
                  </Select.Root>

                  <CardRadioGroup.Button size="medium" variant="secondary" onClick={handleAddonRemoval} fullWidth>
                    {t('TRAVEL_ADDON_PLUS_REMOVE')}
                  </CardRadioGroup.Button>
                </>
              ) : (
                <CardRadioGroup.Button size="medium" variant="secondary" fullWidth>
                  {t('TRAVEL_ADDON_PLUS_ADD')}
                </CardRadioGroup.Button>
              )}
            </Card.Content>
          </ResizablePanel>
        </CardRadioGroup.Item>
      </CardRadioGroup.Root>

      <AddonComparisonCard
        baseTermsVersion={selectedOffer.variant.termsVersion}
        addonTermsVersion={travelAddons[0].variant.termsVersion}
      />
    </section>
  )
}
